import React, { useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  FormFeedback,
} from "reactstrap";
import * as Yup from "yup";
import { useFormik } from "formik";
import { useEffect } from "react";
import ImageCropper from "./ImageCropper";

const AddCategoryModal = ({ isOpen, toggle, onSubmit }) => {
  const [previewImage, setPreviewImage] = useState(null);
  const [cropperOpen, setCropperOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [croppedImageBlob, setCroppedImageBlob] = useState(null);

  const validation = useFormik({
    initialValues: {
      name: "",
      image: null,
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Category name is required"),
      image: Yup.mixed()
        .required("Image is required")
        .test(
          "fileType",
          "Unsupported file format",
          (value) =>
            value &&
            ["image/jpeg", "image/png", "image/jpg", "image/gif"].includes(
              value.type
            )
        ),
    }),

    onSubmit: (values) => {
      const formData = new FormData();
      formData.append("name", values.name);
      formData.append("image", values.image);
      onSubmit(formData);
      toggle();
      validation.resetForm();
    },
  });

  useEffect(() => {
    if (!isOpen) {
      validation.resetForm();
    }
  }, [isOpen]);

  const handleFileChange = (event) => {
    const file = event.currentTarget.files[0];

    validation.setFieldValue("image", file);

    if (file) {
      const imageUrl = URL.createObjectURL(file);
      setSelectedFile(file);
      setPreviewImage(imageUrl);
      setCropperOpen(true);
    } else {
      setPreviewImage(null);
      validation.setFieldTouched("image", true);
    }
  };

  const handleCropComplete = (croppedBlob) => {
    const croppedFile = new File([croppedBlob], "cropped.jpg", {
      type: "image/jpeg",
    });

    validation.setFieldValue("image", croppedFile);
    setCroppedImageBlob(croppedBlob);
    setCropperOpen(false);

    const previewURL = URL.createObjectURL(croppedBlob);
    setPreviewImage(previewURL);
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered scrollable fade={true}>
      <ModalHeader style={{ background: "#E0F8E0" }} toggle={toggle}>
        Add New Category
      </ModalHeader>

      {cropperOpen && previewImage && (
        <Modal
          isOpen={cropperOpen}
          toggle={() => setCropperOpen(false)}
          // size="sm"
          centered
        >
          <ModalHeader toggle={() => setCropperOpen(false)}>
            Crop Image
          </ModalHeader>
          <ModalBody className="text-center mt-3">
            <ImageCropper
              imageSrc={previewImage}
              onCropComplete={handleCropComplete}
              onClose={() => setCropperOpen(false)}
              aspect={16 / 9}
            />
          </ModalBody>
        </Modal>
      )}

      <Form onSubmit={validation.handleSubmit} encType="multipart/form-data">
        <ModalBody>
          <FormGroup>
            <Label for="name">Category Name</Label>
            <Input
              id="name"
              name="name"
              type="text"
              placeholder="Enter category name"
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              value={validation.values.name}
              invalid={validation.touched.name && !!validation.errors.name}
            />
            {validation.touched.name && validation.errors.name ? (
              <FormFeedback>{validation.errors.name}</FormFeedback>
            ) : null}
          </FormGroup>

          <FormGroup>
            <Label for="image">Upload Image</Label>
            <Input
              id="image"
              name="image"
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              onBlur={validation.handleBlur}
              invalid={validation.touched.image && !!validation.errors.image}
            />
            {validation.touched.image && validation.errors.image ? (
              <FormFeedback>{validation.errors.image}</FormFeedback>
            ) : null}
          </FormGroup>

          {previewImage && (
            <div className="mt-3 text-center">
              <img
                src={previewImage}
                alt="Preview"
                style={{
                  maxWidth: "100%",
                  maxHeight: "200px",
                  borderRadius: "8px",
                }}
              />
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" className="btn-sm" onClick={toggle}>
            Cancel
          </Button>
          <Button color="success" className="btn-sm" type="submit">
            Add Category
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default AddCategoryModal;
